import type { SupportedLocale } from "./locales";
import type { i18n } from "i18next";

type TranslationDictionary = Record<string, Record<string, string>>;

type ModuleTranslationLoader<ModuleName extends string> = (locale: SupportedLocale, moduleName: ModuleName) => Promise<TranslationDictionary>;

type EnsureModuleLoaded<ModuleName extends string> = (
  locale: SupportedLocale,
  moduleName: ModuleName,
  loader: ModuleTranslationLoader<ModuleName>,
) => Promise<TranslationDictionary | undefined>;

interface RouteTranslationPreloadOptions<ModuleName extends string> {
  i18n: i18n;
  i18nInitialization: Promise<unknown>;
  defaultLocale: SupportedLocale;
  loadModuleTranslations: ModuleTranslationLoader<ModuleName>;
  ensureModuleLoaded: EnsureModuleLoaded<ModuleName>;
  logger?: Pick<Console, "error">;
}

function getActiveLocale(i18nInstance: i18n, defaultLocale: SupportedLocale): SupportedLocale {
  return (i18nInstance.resolvedLanguage ?? i18nInstance.language ?? defaultLocale) as SupportedLocale;
}

export function createRouteTranslationModulePreloader<ModuleName extends string>(
  options: RouteTranslationPreloadOptions<ModuleName>,
): (moduleName: ModuleName) => Promise<void> {
  const { i18n: i18nInstance, i18nInitialization, defaultLocale, loadModuleTranslations, ensureModuleLoaded, logger = console } = options;

  return async function ensureRouteTranslationModuleLoaded(moduleName: ModuleName): Promise<void> {
    await i18nInitialization;

    const locale = getActiveLocale(i18nInstance, defaultLocale);
    try {
      const moduleTranslations = await ensureModuleLoaded(locale, moduleName, loadModuleTranslations);
      if (moduleTranslations) {
        i18nInstance.addResourceBundle(locale, "translation", moduleTranslations[locale] ?? {}, true, true);
      }
    } catch (error) {
      logger.error(`Error preloading module '${moduleName}' translations for locale ${locale}:`, error);
      throw error;
    }
  };
}
